import React from "react";

/**
 * NBA half-court shot chart, drawn to real-world proportions.
 * Court coord system: 500 units wide × 470 units tall (1 unit = 0.1 ft).
 * Baseline at y=470 (bottom), half-court at y=0 (top).
 * Rim at (250, 417.5) — 5.25 ft from baseline.
 * 3PT arc radius = 237.5 units (23.75 ft).
 * Corner-3 line at x=30 and x=470 (3 ft from sideline).
 * Arc/corner-3 line intersection at y = 417.5 - sqrt(237.5² - 220²) ≈ 328.
 *
 * Zones colored by FG% (cool grey → hot gold).
 */
export default function ShotChart({ zones }) {
  const byName = {};
  (zones || []).forEach(z => { byName[z.name] = z; });

  function colorFor(pct) {
    if (pct === null || pct === undefined) return "#3c3c3c";
    const p = Math.max(0, Math.min(1, pct));
    const t = Math.max(0, Math.min(1, (p - 0.3) / 0.4));
    const lerp = (a, b) => Math.round(a + (b - a) * t);
    return `rgb(${lerp(0x3c, 0xd8)}, ${lerp(0x3c, 0xb8)}, ${lerp(0x3c, 0x54)})`;
  }

  const rim = byName["At the Rim (0-3 ft)"] || {};
  const shortMid = byName["Short Mid (3-10 ft)"] || {};
  const longMid = byName["Long Mid (10-16 ft)"] || {};
  const long2 = byName["Long 2 (16 ft-3P)"] || {};
  const three = byName["3-Point"] || {};
  const corner3 = byName["Corner 3"] || {};

  const fmt = (p) => (p === null || p === undefined ? "—" : `${(p * 100).toFixed(1)}%`);
  const fmtFga = (p) => (p === null || p === undefined ? "—" : `${(p * 100).toFixed(0)}%`);

  // Rim coords
  const RX = 250, RY = 417.5;
  // 3PT arc: center on rim, r=237.5. Corner-3 vertical lines at x=30 and x=470 stop at y=328.
  const ARC_TOP_Y = RY - 237.5;   // 180
  const ARC_CORNER_Y = 328;       // where arc meets corner-3 straight line

  return (
    <div className="border border-white/10 bg-[#1c1c1c] p-6" data-testid="shot-chart">
      <div className="flex items-center gap-3 mb-4">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--org-accent)] font-bold">Shot Chart · Zones (FG%)</div>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        {/* SVG half-court — real proportions */}
        <div>
          <svg viewBox="0 0 500 470" className="w-full h-auto block" data-testid="shot-chart-svg" style={{ maxHeight: 560 }}>
            <defs>
              {/* Clip inside the 3PT area (baseline up to 3PT arc, between corner-3 lines) */}
              <clipPath id="inside3pt">
                <path d={`M 30 470 L 30 ${ARC_CORNER_Y}
                          A 237.5 237.5 0 0 1 470 ${ARC_CORNER_Y}
                          L 470 470 Z`} />
              </clipPath>
              {/* Clip full half-court */}
              <clipPath id="halfcourt">
                <rect x="0" y="0" width="500" height="470" />
              </clipPath>
            </defs>

            {/* Court parquet */}
            <rect x="0" y="0" width="500" height="470" fill="#2f2822" />
            <rect x="0" y="0" width="500" height="470" fill="url(#grain)" opacity="0.4" />
            {/* Subtle grain */}
            <pattern id="grain" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M0 5 L10 5" stroke="#3a2f26" strokeWidth="0.5" />
            </pattern>

            {/* ==== ZONE FILLS ==== */}
            {/* 3-Point (outside arc) — fills whole court, then covered by inside-arc */}
            <rect x="0" y="0" width="500" height="470" fill={colorFor(three.fg_pct)} opacity="0.75" />

            {/* Corner-3 rectangles (extra emphasis) */}
            <rect x="0" y={ARC_CORNER_Y} width="30" height={470 - ARC_CORNER_Y} fill={colorFor(corner3.fg_pct)} opacity="0.85" />
            <rect x="470" y={ARC_CORNER_Y} width="30" height={470 - ARC_CORNER_Y} fill={colorFor(corner3.fg_pct)} opacity="0.85" />

            {/* Inside-arc zones — layered concentric circles clipped to 3PT area */}
            <g clipPath="url(#inside3pt)">
              {/* Long 2 (16 ft - 3P): fill full arc region, then covered by inner zones */}
              <rect x="0" y="0" width="500" height="470" fill={colorFor(long2.fg_pct)} opacity="0.9" />
              {/* Long mid (10-16 ft): circle r=160 */}
              <circle cx={RX} cy={RY} r="160" fill={colorFor(longMid.fg_pct)} opacity="0.95" />
              {/* Short mid (3-10 ft): circle r=100 */}
              <circle cx={RX} cy={RY} r="100" fill={colorFor(shortMid.fg_pct)} />
              {/* Rim (0-3 ft): circle r=30 */}
              <circle cx={RX} cy={RY} r="30" fill={colorFor(rim.fg_pct)} />
            </g>

            {/* ==== COURT LINES (on top) ==== */}
            <g stroke="#f0e2b0" fill="none" strokeWidth="2">
              {/* Sidelines & baseline */}
              <line x1="0" y1="470" x2="500" y2="470" />
              <line x1="0" y1="0" x2="0" y2="470" />
              <line x1="500" y1="0" x2="500" y2="470" />
              {/* Half-court line at top */}
              <line x1="0" y1="0" x2="500" y2="0" />
              {/* 3PT arc + corner-3 lines */}
              <path d={`M 30 470 L 30 ${ARC_CORNER_Y}
                        A 237.5 237.5 0 0 1 470 ${ARC_CORNER_Y}
                        L 470 470`} />
            </g>

            {/* Painted key (lane) — 16 ft wide × 19 ft deep. Backboard at y=430, FT line at y=280. */}
            <g stroke="#f0e2b0" strokeWidth="1.5">
              <rect x="170" y="280" width="160" height="190" fill="rgba(0,0,0,0.15)" />
              {/* Lane blocks */}
              <line x1="170" y1="330" x2="150" y2="330" />
              <line x1="170" y1="360" x2="150" y2="360" />
              <line x1="330" y1="330" x2="350" y2="330" />
              <line x1="330" y1="360" x2="350" y2="360" />
              {/* Free throw circle — top half solid, bottom half dashed */}
              <path d="M 190 280 A 60 60 0 0 1 310 280" fill="none" />
              <path d="M 310 280 A 60 60 0 0 1 190 280" fill="none" strokeDasharray="6 4" />
              {/* Restricted area arc (4 ft radius around rim) */}
              <path d={`M ${RX - 40} ${RY} A 40 40 0 0 0 ${RX + 40} ${RY}`} fill="none" />
              {/* Backboard */}
              <line x1={RX - 30} y1="430" x2={RX + 30} y2="430" strokeWidth="3" />
              {/* Rim */}
              <circle cx={RX} cy={RY} r="9" fill="none" strokeWidth="2" />
              {/* Center-circle sliver at top */}
              <path d="M 190 0 A 60 60 0 0 0 310 0" fill="none" />
            </g>

            {/* ==== LABELS ==== */}
            <ZoneLabel x={RX} y={RY - 2} text="RIM" pct={rim.fg_pct} fga={rim.pct_fga} fmt={fmt} fmtFga={fmtFga} small />
            <ZoneLabel x={RX} y={370} text="3-10 FT" pct={shortMid.fg_pct} fga={shortMid.pct_fga} fmt={fmt} fmtFga={fmtFga} />
            <ZoneLabel x={RX} y={295} text="10-16 FT" pct={longMid.fg_pct} fga={longMid.pct_fga} fmt={fmt} fmtFga={fmtFga} />
            <ZoneLabel x={RX} y={225} text="16FT — 3P" pct={long2.fg_pct} fga={long2.pct_fga} fmt={fmt} fmtFga={fmtFga} />
            <ZoneLabel x={RX} y={90} text="3-POINT" pct={three.fg_pct} fga={three.pct_fga} fmt={fmt} fmtFga={fmtFga} />
            <ZoneLabel x={70} y={400} text="CORNER 3" pct={corner3.fg_pct} fga={corner3.pct_fga_of_3pa} label2="of 3PA" fmt={fmt} fmtFga={fmtFga} small />
            <ZoneLabel x={430} y={400} text="CORNER 3" pct={corner3.fg_pct} fga={corner3.pct_fga_of_3pa} label2="of 3PA" fmt={fmt} fmtFga={fmtFga} small />
          </svg>

          {/* Color legend */}
          <div className="mt-3 flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#a0a0a0]">
            <span>30%</span>
            <div className="flex-1 h-2" style={{ background: "linear-gradient(to right, #3c3c3c, #d8b854)" }} />
            <span>70%+</span>
          </div>
        </div>

        {/* Zone table */}
        <div className="space-y-2" data-testid="zone-table">
          {(zones || []).map((z, i) => {
            const share = z.pct_fga ?? z.pct_fga_of_3pa;
            return (
              <div key={i} className="border border-white/10 bg-[#101010] p-3" data-testid={`zone-row-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-widest text-white">{z.name}</div>
                  <div className="font-heading text-2xl text-[var(--org-accent)]">{fmt(z.fg_pct)}</div>
                </div>
                <div className="w-full h-1.5 bg-[#141414] mb-1">
                  <div className="h-full bg-[var(--org-accent)]" style={{ width: `${Math.max(2, (z.fg_pct || 0) * 100)}%` }} />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#757575]">
                  {share !== null && share !== undefined ? `${(share * 100).toFixed(1)}% ${z.name === "Corner 3" ? "of 3PA" : "of FGA"}` : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ZoneLabel({ x, y, text, pct, fga, label2, fmt, fmtFga, small }) {
  const sizeText = small ? 8 : 10;
  const sizePct = small ? 11 : 14;
  const sizeSub = small ? 8 : 9;
  return (
    <g style={{ pointerEvents: "none" }}>
      <rect x={x - 42} y={y - sizeText - 4} width="84" height={sizeText + sizePct + sizeSub + 8}
            fill="rgba(35,35,35,0.65)" rx="2" />
      <text x={x} y={y - 2} textAnchor="middle" fill="#ffffff" fontSize={sizeText} fontFamily="JetBrains Mono" letterSpacing="1">
        {text}
      </text>
      <text x={x} y={y + sizePct} textAnchor="middle" fill="#d8b854" fontSize={sizePct} fontWeight="bold" fontFamily="JetBrains Mono">
        {fmt(pct)}
      </text>
      <text x={x} y={y + sizePct + sizeSub + 2} textAnchor="middle" fill="#c0c0c0" fontSize={sizeSub} fontFamily="JetBrains Mono">
        {fmtFga(fga)} {label2 || "of FGA"}
      </text>
    </g>
  );
}
